import {entry as castEntry, objectFromEntries} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import isArchived from 'archive/isArchived';
import {TagSchema} from 'constants/RegistryConstants';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import * as RegistryUtil from 'registry/RegistryUtil';
import {schemaFieldsetName} from 'schemas/schema-interface/util/schemaFieldsetName';
import {getTagSchemaTypeName} from 'tag-schema/util/TagSchemaUtil';
import EditableItemTable, {RowProps} from 'ui-library/EditableItemTable';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {SchemaInterface} from './SchemaInterfaceConstants';

export interface ImplementingSchemasTableProps {
  interface: SchemaInterface | null;
}

export default function ImplementingSchemasTable(props: ImplementingSchemasTableProps): JSX.Element {
  const columnHeaders = [
    {name: 'Schemas', width: 4},
    {name: 'Entity Type', width: 2},
    {name: 'Entity Fields', width: 6},
  ];
  const isLoading = props.interface == null;
  const schemas =
    props.interface?.schemas
      ?.map((queryTagSchema) => graphQLToREST('TagSchema', queryTagSchema))
      ?.sort(({name: name1}, {name: name2}) => name1.toLowerCase().localeCompare(name2.toLowerCase())) ?? [];

  // have to cast mapping function output because typescript treats [s.id, i] as ReactText[]
  const schemaIndicesById = objectFromEntries(schemas.map((s, i) => castEntry(s.id, i)));

  const mapSchemaToRowValue = (schema: TagSchema): Array<JSX.Element | string> => [
    <div>
      <BenchlingIcon className="typeIcon u-small-spacing-left" icon={schema.iconId} />
      <span className="u-small-spacing-left">
        {schema.name}
        <ArchiveRecordLabel item={schema} />
      </span>
    </div>,
    getTagSchemaTypeName({schema}),
    // eslint-disable-next-line no-restricted-syntax
    _.pluck(schema.fields, 'name').join(', '),
  ];
  const rows: Array<RowProps> = schemas.map((schema: TagSchema) => {
    const row: RowProps = {
      id: schema.id,
      itemIndex: schemaIndicesById[schema.id],
      isArchived: isArchived(schema),
      values: mapSchemaToRowValue(schema),
    };
    return row;
  });

  const getSchemaUrlForRow = (itemIndex: number): string | null => {
    if (props.interface != null) {
      const schema = schemas[itemIndex];
      return RegistryUtil.getTagSchemaSettingsUrl(
        props.interface.organization.urlIdentifier,
        schema.registryId,
        schema.id
      );
    } else {
      return null;
    }
  };

  return (
    <div className="settingsPage-contentWrapper">
      <div className="u-large-spacing-bottom">
        <p>{`This ${schemaFieldsetName()} has been applied to the following entity schemas:`}</p>
      </div>
      <EditableItemTable
        columnHeaders={columnHeaders}
        emptyMessage={`There are no schemas implementing this ${schemaFieldsetName()}.`}
        isLoading={isLoading}
        rows={rows}
        getEditUrl={getSchemaUrlForRow}
        writable={false}
        leftSubheaderNode={null}
      />
    </div>
  );
}
